import { useState } from 'react';
import { 
  collection, 
  getDocs, 
  addDoc, 
  query, 
  where,
  writeBatch,
  Timestamp,
  deleteDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../firebase/config';

export default function MigrateData() {
  const [migrating, setMigrating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    successful: 0,
    failed: 0,
    deleted: 0,
    lastError: null
  });

  const parseAmount = (amount) => {
    if (typeof amount === 'number') return amount;
    if (!amount) return 0;
    
    // Ha string, eltávolítjuk a + jelet, USDT-t és egyéb karaktereket
    if (typeof amount === 'string') {
      const cleanAmount = amount.replace(/[+USDT\s,]/g, '');
      return parseFloat(cleanAmount) || 0;
    }
    
    return 0;
  };

  const parseTimestamp = (dateStr) => {
    if (!dateStr) return Timestamp.now();
    try {
      // Ha Z-vel végződő ISO string
      if (dateStr.endsWith('Z')) {
        return Timestamp.fromDate(new Date(dateStr));
      }
      // Ha tartalmaz T-t és időzónát
      if (dateStr.includes('T')) {
        return Timestamp.fromDate(new Date(dateStr));
      }
      // Ha "Dec 03 2024 09:56" formátumú
      return Timestamp.fromDate(new Date(dateStr));
    } catch (error) {
      console.error('Date parsing error:', error);
      return Timestamp.now();
    }
  };

  const cleanHtmlTags = (str) => {
    if (typeof str !== 'string') return '';
    return str.replace(/<[^>]*>/g, '').trim();
  };

  const parseTransactions = (transactions) => {
    if (!Array.isArray(transactions)) return [];
    
    return transactions.map(tx => ({
      transaction_id: tx.tnx || tx.id?.toString(),
      amount: parseAmount(tx.amount || tx.final_amount),
      type: tx.type || 'Unknown',
      method: tx.method || 'System',
      status: tx.status ? cleanHtmlTags(tx.status) : 'Unknown',
      created_at: parseTimestamp(tx.created_at),
      description: tx.description || ''
    }));
  };

  const handleMigration = async () => {
    if (!window.confirm('Are you sure you want to migrate all users to customers collection?')) {
      return;
    }

    setMigrating(true);
    setProgress(0);
    setStats({
      total: 0,
      processed: 0,
      successful: 0,
      failed: 0,
      deleted: 0,
      lastError: null
    });

    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const total = usersSnapshot.docs.length;

      setStats(prev => ({ ...prev, total }));

      const batchSize = 500;
      let batch = writeBatch(db);
      let batchCount = 0;

      for (const userDoc of usersSnapshot.docs) {
        try {
          const userData = userDoc.data();

          const customerData = {
            id: userData.id || parseInt(userDoc.id),
            username: userData.username || '',
            email: userData.email || '',
            first_name: userData.first_name || '',
            last_name: userData.last_name || '',
            full_name: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
            avatar: userData.avatar || '',
            country: userData.country || '',
            phone: userData.phone || '',
            city: userData.city || '',
            address: userData.address || '',
            zip_code: userData.zip_code || '',
            gender: userData.gender || '',
            date_of_birth: userData.date_of_birth || null,
            
            // Pénzügyi adatok
            balance: parseAmount(userData.balance),
            total_profit: parseAmount(userData.total_profit || userData.profit_balance),
            total_deposit: parseAmount(userData.total_deposit),
            total_invest: parseAmount(userData.total_invest),
            
            // Státuszok
            status: userData.status || 'Active',
            kyc: userData.kyc || 'Unverified',
            kyc_time: userData.kyc_time || '',
            kyc_type: userData.kyc_type || '',
            kyc_credential: userData.kyc_credential || null,
            
            // Rendszer beállítások
            deposit_status: !!userData.deposit_status,
            withdraw_status: !!userData.withdraw_status,
            transfer_status: !!userData.transfer_status,
            two_fa: !!userData.two_fa,
            email_verified_at: parseTimestamp(userData.email_verified_at),
            
            // Időbélyegek
            created_at: parseTimestamp(userData.created_at),
            updated_at: parseTimestamp(userData.updated_at),
            
            // Referral
            ref_id: userData.ref_id || null,
            ranking_id: userData.ranking_id || 1,
            rankings: userData.rankings || '[1]',
            
            // Tranzakciók
            transactions: parseTransactions(userData.transactions),
            
            // Migrációs mezők
            migrated_from: userDoc.id,
            migrated_at: Timestamp.now()
          };

          // Add to customers collection
          const customersRef = collection(db, 'customers');
          await addDoc(customersRef, customerData);

          setStats(prev => ({
            ...prev,
            processed: prev.processed + 1,
            successful: prev.successful + 1
          }));

          batchCount++;

          if (batchCount === batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          }

          setProgress((stats.processed / total) * 100);
        } catch (error) {
          console.error(`Error migrating user ${userDoc.id}:`, error);
          setStats(prev => ({
            ...prev,
            processed: prev.processed + 1,
            failed: prev.failed + 1,
            lastError: `Error with user ${userDoc.id}: ${error.message}`
          }));
        }
      }

      if (batchCount > 0) {
        await batch.commit();
      }

    } catch (error) {
      console.error('Migration error:', error);
      setStats(prev => ({
        ...prev,
        lastError: `Migration error: ${error.message}`
      }));
    } finally {
      setMigrating(false);
    }
  };

  const handleDeleteMigrated = async () => {
    if (!window.confirm('Are you sure you want to delete all migrated users? This cannot be undone!')) {
      return;
    }

    setDeleting(true);
    setProgress(0);

    try {
      // Lekérjük a sikeresen migrált felhasználókat
      const customersRef = collection(db, 'customers');
      const migratedQuery = query(customersRef, where('migrated_from', '!=', null));
      const migratedDocs = await getDocs(migratedQuery);

      const total = migratedDocs.docs.length;
      let deleted = 0;

      for (const customerDoc of migratedDocs.docs) {
        try {
          const migratedFromId = customerDoc.data().migrated_from;
          
          // Töröljük az eredeti user dokumentumot
          await deleteDoc(doc(db, 'users', migratedFromId));
          
          deleted++;
          setProgress((deleted / total) * 100);
          setStats(prev => ({ ...prev, deleted }));
        } catch (error) {
          console.error('Error deleting user:', error);
          setStats(prev => ({
            ...prev,
            lastError: `Error deleting user: ${error.message}`
          }));
        }
      }

      alert(`Successfully deleted ${deleted} migrated users`);
    } catch (error) {
      console.error('Delete error:', error);
      setStats(prev => ({
        ...prev,
        lastError: `Delete error: ${error.message}`
      }));
    } finally {
      setDeleting(false);
      setProgress(0);
    }
  };

  return (
    <div className="p-4 bg-white rounded-lg shadow">
      <h2 className="text-lg font-semibold mb-4">Migrate Users to Customers</h2>
      
      <div className="flex flex-col space-y-4">
        <div className="flex space-x-4">
          <button
            onClick={handleMigration}
            disabled={migrating || deleting}
            className={`px-4 py-2 rounded-lg text-white ${
              migrating || deleting
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {migrating ? 'Migrating...' : 'Start Migration'}
          </button>

          <button
            onClick={handleDeleteMigrated}
            disabled={migrating || deleting || stats.successful === 0}
            className={`px-4 py-2 rounded-lg text-white ${
              migrating || deleting || stats.successful === 0
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {deleting ? 'Deleting...' : 'Delete Migrated Users'}
          </button>
        </div>

        {(migrating || deleting) && (
          <div className="flex items-center space-x-2">
            <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm text-gray-600 w-20">
              {Math.round(progress)}%
            </span>
          </div>
        )}

        {/* Migration Statistics */}
        {stats.total > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-semibold mb-2">Migration Status:</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p>Total Records: {stats.total}</p>
                <p>Processed: {stats.processed}</p>
              </div>
              <div>
                <p className="text-green-600">Successful: {stats.successful}</p>
                <p className="text-red-600">Failed: {stats.failed}</p>
                {stats.deleted > 0 && (
                  <p className="text-orange-600">Deleted: {stats.deleted}</p>
                )}
              </div>
            </div>
            {stats.lastError && (
              <div className="mt-2 text-sm text-red-600">
                Last Error: {stats.lastError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 